import React from "react";

import type { PlanNamesType } from "@hz/stealthy-gmc-cloaker-common/constants";
import type { PlanType } from "@hz/stealthy-gmc-cloaker-common/plans";
import { features, plans } from "@hz/stealthy-gmc-cloaker-common/plans";

import { Check, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Icon,
  List,
  ListIcon,
  ListItem,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";

import { useUpgradePlanMutation } from "@state/apiSlice/requests";
import type { AppDispatch } from "@state/store";
import { fetchInit, selectUserState } from "@state/userSlice/userSlice";

// interface UpgradePlanSelectionProps {
//   onSelectPlan: (plan: PlanNamesType) => void;
//   onClose: () => void;
//   showSteps?: boolean;
// }

export const UpgradePlanSelection = () => {
  const { billing } = useSelector(selectUserState);
  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [upgradePlan] = useUpgradePlanMutation();

  const currentPlan = plans.find((p) => p.name === billing?.plan);
  // A plan that is set but missing from `plans` is a custom top-tier plan (e.g. C4_5M),
  // so there is nothing to upgrade to. Only offer the full list when no plan is set yet.
  const upgradePlans = currentPlan
    ? plans.filter((p) => p.rank > currentPlan.rank)
    : billing?.plan
      ? []
      : plans;

  const getButtonName = (plan: PlanType) => {
    if (billing?.plan === plan.name) return "Current Plan";
    return `Upgrade to ${plan.name}`;
  };

  const upgradePlanHandler = async (planName: PlanNamesType) => {
    try {
      const result = await upgradePlan({ newPlan: planName }).unwrap();

      if (result.success) {
        toast({
          title: `Plan upgraded to ${planName}`,
          status: "success",
          duration: 5000,
          position: "bottom-right",
        });

        await dispatch(fetchInit({ forceRefetch: true })).unwrap();

        navigate("/");
      }
    } catch (err) {
      toast({
        title: "Error upgrading plan",
        description: "Please try again later",
        status: "error",
        duration: 5000,
        position: "bottom-right",
      });
    }
  };

  return (
    <Box
      bg="gray.900"
      minH="100vh"
      p={8}
      width="100%"
    >
      <Container maxW="container.4xl">
        <VStack
          spacing={6}
          mb={12}
          textAlign="center"
        >
          <Heading
            size="xl"
            color="white"
          >
            💼 Upgrade Your Plan
          </Heading>
          <Text
            color="gray.300"
            maxW="800px"
          >
            All plans include full StealthyGMC protection, expert setup by our team, and stealth rules
            tailored to your store.
          </Text>
        </VStack>

        {upgradePlans.length === 0 ? (
          <Text
            color="gray.400"
            textAlign="center"
          >
            You’re already on the highest plan. 🚀
          </Text>
        ) : (
          <SimpleGrid
            columns={{ base: 1, lg: upgradePlans.length }}
            spacing={8}
            mb={12}
          >
            {upgradePlans.map((plan) => (
              <Box
                key={plan.name}
                bg="#1A1A22"
                border="1px solid"
                borderColor="#4285f4"
                borderRadius="lg"
                p={6}
                position="relative"
                transition="all 0.3s"
                _hover={{
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 24px -10px rgba(0, 0, 0, 0.3)",
                  borderColor: "#4285f4",
                }}
              >
                <VStack
                  align="stretch"
                  spacing={5}
                >
                  <Heading
                    size="md"
                    color="white"
                  >
                    {plan.emoji} {plan.name}
                  </Heading>
                  <Box>
                    <Text
                      fontSize="3xl"
                      fontWeight="bold"
                      color="white"
                    >
                      ${plan.price}
                      <Text
                        as="span"
                        fontSize="sm"
                        color="gray.400"
                      >
                        /mo
                      </Text>
                    </Text>
                    <Text
                      fontSize="sm"
                      color="gray.400"
                    >
                      {plan.sessions} monthly sessions*
                    </Text>
                  </Box>
                  <List spacing={3}>
                    {features.map((feature, index) => (
                      <ListItem
                        key={index}
                        display="flex"
                        alignItems="flex-start"
                      >
                        <ListIcon
                          as={Check}
                          color="#3182ce"
                          mt={1}
                        />
                        <Text
                          fontSize="sm"
                          color="gray.300"
                        >
                          {feature}
                        </Text>
                      </ListItem>
                    ))}
                  </List>
                  <Button
                    size="lg"
                    width="100%"
                    bg="#4285f4"
                    color="white"
                    // disabled={true}
                    _hover={{
                      bg: "#357ae8",
                      boxShadow: "0 0 10px rgba(66, 133, 244, 0.6)",
                    }}
                    // onClick={() => upgradePlanHandler(plan.name)}
                  >
                    {/* {getButtonName(plan)} */}
                    CONTACT SUPPORT
                  </Button>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        )}

        <Text
          fontSize="xs"
          color="gray.500"
          textAlign="center"
          maxW="800px"
          mx="auto"
          mb={10}
        >
          *A billable session is every visitor interaction with your website that begins when a user first
          loads a page containing our code and continues until the browser tab is closed. Unused sessions do
          not carry forward.
        </Text>
      </Container>
    </Box>
  );
};
